import { useEffect, useId, useState } from 'react';
import { useLocation } from 'react-router';
import { useNarrowView } from '../../lib/media';
import { PANEL_ID, useAssistant } from './AssistantProvider';
import { Hana } from './Hana';
import { useOwnAppearance } from './api';
import { useNudges } from './proactive/api';
import { HanaBubble, bubbleSeen, markBubbleSeen, pickBubble, type BubblePick } from './proactive/HanaBubble';

/** Spec §7.4: the badge shows at most "9+". */
export const badgeText = (n: number): string => (n > 9 ? '9+' : String(n));

/**
 * The header control between search and the bell (spec §8.1): Hana herself, 28 px, with the panel's face. S3 L2 adds
 * the nudge badge (§7.4: the shown nudges, hidden at 0 and while nudges are off) and, from lg, the one bubble a session
 * (D73), during which Hana wears the bubble's face.
 */
export function AssistantButton() {
  const appearance = useOwnAppearance();
  const {
    prefs, principal, open, setOpen, toggle, emotion, still, buttonRef, appOpenSettled, appOpenBubble,
  } = useAssistant();
  const name = `Trợ lý ${prefs.personaName}`;
  const { data } = useNudges(true);
  const on = data?.enabled === true;
  const count = on ? data.badgeCount : 0;
  const narrow = useNarrowView();
  const countId = useId();
  const userId = principal.userId;

  const { pathname } = useLocation();

  // L3 D123: nothing is picked before app-open answers, so the briefing's bubble it may grant takes the session's one
  // slot ahead of a nudge already in the tray.
  const pick = appOpenSettled ? pickBubble(on ? data.items : [], pathname === '/' ? null : appOpenBubble) : null;
  const [bubble, setBubble] = useState<BubblePick | null>(null);
  const candidate = pick === null ? null : `${pick.kind}:${pick.id}`;
  useEffect(() => {
    if (pick === null || open || narrow || bubble !== null || bubbleSeen(userId)) return;
    markBubbleSeen(userId);
    setBubble(pick);
    // `pick` is read through its key: a refetch with the same nudge is not a new bubble.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidate, open, narrow, userId]);
  useEffect(() => {
    if (open || narrow) setBubble(null);
  }, [open, narrow]);
  // The Dashboard shows the briefing itself: arriving there, by the bubble's link or otherwise, ends its bubble.
  useEffect(() => {
    if (pathname === '/') setBubble((b) => (b?.kind === 'briefing' ? null : b));
  }, [pathname]);

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        aria-label={name}
        aria-describedby={count > 0 ? countId : undefined}
        title={`${name} (⌘J)`}
        aria-keyshortcuts="Meta+J Control+J"
        aria-expanded={open}
        aria-controls={PANEL_ID}
        onClick={toggle}
        className="relative flex size-9 items-center justify-center rounded-lg transition-colors hover:bg-[hsl(var(--h-muted))]"
      >
        <Hana appearance={appearance} emotion={bubble?.emotion ?? emotion} size={28} still={still} />
        {count > 0 && (
          <span data-nudge-badge="" aria-hidden="true"
            className="absolute -right-1.5 -top-1 flex h-4.5 min-w-4.5 items-center justify-center rounded-full border-2 border-[hsl(var(--h-card))] bg-[hsl(var(--h-error))] px-1 text-[10px] font-bold leading-none text-[hsl(var(--h-primary-foreground))]">
            {badgeText(count)}
          </span>
        )}
      </button>
      {count > 0 && <span id={countId} hidden>{`${count} lời nhắc`}</span>}
      {bubble !== null && !narrow && !open && (
        <HanaBubble pick={bubble} still={still} onClose={() => setBubble(null)}
          onOpen={() => { setBubble(null); setOpen(true); }} onDashboard={() => setBubble(null)} />
      )}
    </div>
  );
}
